'use client';

import { useEffect } from 'react';
import { useQueryClient } from '@zg-rentals/react-query5';

import type { BoundaryData } from '@/src/lib/api';
import {
  mapStateKeys,
  useClearPendingMapAreaNavigation,
  useSetMapBorderState,
  useSetMapPosition,
} from '@/src/lib/hooks';
import type { MapBorderState, MapState } from '@/src/lib/hooks/map/mapStateKeys';
import { hasUsableEncodedBoundary } from '@/src/lib/map/boundaryAvailability';
import { resolveInitialShowBorder } from '@/src/lib/map/borderPreference';
import type { MapPositionFromViewport } from '@/src/lib/map/viewportBounds';
import { buildInitialSrpMapState, type MapStateFallbackBounds } from './buildInitialSrpMapState';

export type { MapStateFallbackBounds } from './buildInitialSrpMapState';

type Props = {
  areaKey?: string;
  resourceId?: string;
  boundary: BoundaryData | null;
  initialMapPosition: MapPositionFromViewport | null;
  fallbackBounds?: MapStateFallbackBounds;
  initialShowBorder?: boolean;
  /** True when the URL carried a `border` value, which outranks the stored preference. */
  hasExplicitBorderParam?: boolean;
  /** Forces the border off: the area searches its map box instead of clipping to the scope. */
  hasMissingBoundary?: boolean;
  waitForStreamedBoundary?: boolean;
};

function buildAreaKey(
  areaKey: string | undefined,
  fallbackBounds: MapStateFallbackBounds | undefined,
): string | undefined {
  if (areaKey) return areaKey;
  if (!fallbackBounds) return undefined;
  return `${fallbackBounds.minLat}:${fallbackBounds.maxLat}:${fallbackBounds.minLon}:${fallbackBounds.maxLon}`;
}

/**
 * Seeds the map shell's position + border from server-resolved boundary data.
 *
 * Position priority:
 *   1. URL-derived `initialMapPosition` (e.g. `?lat&lon&z`) — wins immediately.
 *   2. Boundary-derived position when the area boundary resolved.
 *   3. `fallbackBounds` (area envelope) when boundary fetch failed — keeps the
 *      desktop map from getting stuck without a position.
 *
 * Only seeds position if the cache doesn't already hold one, or if the cached
 * position was seeded for another area. The user's same-area pan/zoom state
 * survives SRP ↔ HDP transitions without letting cross-area SRP navigation
 * reuse stale bounds. Border/boundary is always written when coords are
 * present because the area can change per navigation.
 *
 * Markers and pinned-marker are owned by LiveListingsGrid.
 */
export function MapStateHydrator({
  areaKey: initialAreaKey,
  resourceId,
  boundary,
  initialMapPosition,
  fallbackBounds,
  initialShowBorder = true,
  hasExplicitBorderParam = false,
  hasMissingBoundary = false,
  waitForStreamedBoundary = true,
}: Props) {
  const queryClient = useQueryClient();
  const setMapPosition = useSetMapPosition();
  const setMapBorderState = useSetMapBorderState();
  const clearPendingMapAreaNavigation = useClearPendingMapAreaNavigation();
  const areaKey = buildAreaKey(initialAreaKey, fallbackBounds);

  useEffect(() => {
    const boundaryCoordinates = boundary?.coordinates;
    const coords = hasUsableEncodedBoundary(boundaryCoordinates) ? boundaryCoordinates : undefined;
    const initialMapState = buildInitialSrpMapState({
      boundary,
      initialMapPosition,
      fallbackBounds,
    });

    if (initialMapPosition && initialMapState) {
      setMapPosition(
        initialMapState.bounds,
        initialMapState.center,
        initialMapState.zoom,
        initialMapState.source,
        areaKey,
      );
    } else {
      const existing = queryClient.getQueryData<MapState>(mapStateKeys.position());
      const isStaleFromOtherArea = !!existing && areaKey != null && existing.areaKey !== areaKey;

      if ((!existing || isStaleFromOtherArea) && initialMapState) {
        setMapPosition(
          initialMapState.bounds,
          initialMapState.center,
          initialMapState.zoom,
          initialMapState.source,
          areaKey,
        );
      }
    }

    const existingBorder = queryClient.getQueryData<MapBorderState>(mapStateKeys.border());
    const existingBoundaryIsForArea =
      !!areaKey && existingBorder?.boundaryAreaId === areaKey && !!existingBorder.encodedBoundary;
    const shouldWaitForStreamedBoundary = waitForStreamedBoundary && !!initialMapPosition && !coords;

    setMapBorderState({
      // Effect-only read: the stored preference never reaches SSR markup, so it
      // can't cause a hydration mismatch.
      showBorder: !hasMissingBoundary && resolveInitialShowBorder(initialShowBorder, hasExplicitBorderParam),
      isDetecting: false,
      encodedBoundary: coords ?? (existingBoundaryIsForArea ? existingBorder.encodedBoundary : null),
      isBoundaryLoading: shouldWaitForStreamedBoundary && !existingBoundaryIsForArea,
      boundaryAreaId: initialAreaKey ?? null,
      hasMissingBoundary,
    });

    clearPendingMapAreaNavigation(resourceId);
  }, [
    areaKey,
    initialAreaKey,
    boundary,
    clearPendingMapAreaNavigation,
    initialMapPosition,
    fallbackBounds,
    initialShowBorder,
    hasExplicitBorderParam,
    hasMissingBoundary,
    queryClient,
    resourceId,
    setMapPosition,
    setMapBorderState,
    waitForStreamedBoundary,
  ]);

  return null;
}
